import { useTranslations } from 'next-intl'

import classNames from 'classnames'

import IconTabs from '~/components/blocks/IconTabs/IconTabs'
import { ComponentTheme } from '~/components/themes'
import { useSettingsContext } from '~/contexts/settingsContext'
import analyticsPusher, { analyticsEventTypes } from '~/utils/analyticsPusher'

import Container, { gutterTypes } from '../Container/Container'
import Fixture from '../Fixture/Fixture'
import type { IconTypes } from '../Icon/Icon'
import Link from '../Link/Link'
import Text, { TextSizes } from '../Text/Text'

import styles from './ContactTabs.module.css'

export interface ContactTabsProps {
  theme?: ComponentTheme
  verticalPadding?: boolean
  noPaddingBottom?: boolean
  testID?: string
  moduleId?: string
}

export default function ContactTabs({
  testID,
  theme = ComponentTheme.primary,
  verticalPadding,
  noPaddingBottom,
  moduleId,
}: ContactTabsProps) {
  const { mabPhone, mabPhoneUS, mabEmail } = useSettingsContext()
  const t = useTranslations('contactTabs')
  const GATagEmail = 'Contact: Email link clicked'
  const GATagPhone = 'Contact: Phone number clicked'

  const clickEventPush = (name: string) => {
    analyticsPusher(analyticsEventTypes.gaEvent, {
      gaEventName: name,
    })
  }

  const emailAction = () => {
    clickEventPush(GATagEmail)
    window.open(`mailto:${mabEmail}`)
  }

  // Props hardcoded for now. May make dynamic and controlled in page builder down the line.

  const items = [
    {
      icon: 'SpeechBubble' as IconTypes,
      title: t('socialMedia.title'),
      text: (
        <div className={classNames(styles.textContainer)}>
          <Text size={TextSizes.bodyMed}>
            <p className="prose">
              {t('socialMedia.description')} <br />
              <Link
                href="https://www.instagram.com/myartbroker/?hl=en"
                target="_blank"
              >
                Instagram
              </Link>
              ,{' '}
              <Link
                href="https://www.facebook.com/myartbroker/"
                target="_blank"
              >
                Facebook
              </Link>{' '}
              &amp;{' '}
              <Link
                href="https://www.linkedin.com/company/9443041/admin/"
                target="_blank"
              >
                LinkedIn
              </Link>
            </p>
          </Text>
        </div>
      ),
    },
    {
      icon: 'Email' as IconTypes,
      title: t('email.title'),
      text: (
        <div className={classNames(styles.textContainer)}>
          <Text size={TextSizes.bodyMed}>
            <p className="prose">
              {t('email.description')}{' '}
              <Link
                href={`mailto:${mabEmail}`}
                onClick={() => clickEventPush(GATagEmail)}
              >
                {mabEmail}
              </Link>{' '}
              {t('email.promise')}
            </p>
          </Text>
        </div>
      ),
      buttonText: t('email.button'),
      buttonAction: emailAction,
    },

    {
      icon: 'Call' as IconTypes,
      title: t('telephone.title'),
      text: (
        <div className={classNames(styles.textContainer)}>
          <Text size={TextSizes.bodyMed}>
            <p className={classNames(styles.contactText, 'prose')}>
              {t('telephone.description')}
            </p>
            <p className={classNames('prose', styles.mabPhone)}>
              <Link
                href={`tel:${mabPhone}`}
                className="no-underline"
                onClick={() => clickEventPush(GATagPhone)}
              >
                <strong>{t('telephone.locationLondon')}</strong> {mabPhone}
              </Link>
            </p>
            {mabPhoneUS && (
              <p className={classNames('prose', styles.mabPhone)}>
                <Link
                  href={`tel:${mabPhone}`}
                  className="no-underline"
                  onClick={() => clickEventPush(GATagPhone)}
                >
                  <strong>{t('telephone.locationNewYork')}</strong> {mabPhoneUS}
                </Link>
              </p>
            )}
          </Text>
        </div>
      ),
    },
  ]

  return (
    <Fixture
      theme={theme}
      verticalPadding={verticalPadding}
      noPaddingBottom={noPaddingBottom}
      id={moduleId}
    >
      <Container gutters={gutterTypes.always}>
        <div data-testid={testID}>
          <IconTabs items={items} />
        </div>
      </Container>
    </Fixture>
  )
}
